import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2, Mail, MapPin } from "lucide-react";
import { submitContactInquiry } from "../lib/api";
import { INDUSTRIES } from "../data/industries";

const PROJECT_TYPES = [
    "Pilot project",
    "OEM integration",
    "Infrastructure deployment",
    "Licensing / joint development",
    "Research collaboration",
    "Other",
];

const POWER_RANGES = [
    "Up to 11 kW (current platform)",
    "11 kW",
    "Higher than 11 kW (future / roadmap)",
    "Not sure yet",
];

const emptyForm = {
    name: "",
    email: "",
    company: "",
    country: "",
    industry: INDUSTRIES[0].title,
    project_type: PROJECT_TYPES[0],
    power_range: POWER_RANGES[0],
    use_case: "",
    phone: "",
};

const Contact = () => {
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            await submitContactInquiry(form);
            setSubmitted(true);
            setForm(emptyForm);
            toast.success("Inquiry received — our team will be in touch.");
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                (Array.isArray(err?.response?.data?.detail)
                    ? err.response.data.detail.map((d) => d.msg).join(", ")
                    : null);
            toast.error(detail || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="contact-page" className="bg-[#050B14] text-white min-h-screen">
            <section className="relative pt-32 pb-24 lg:pb-32 overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-40" />
                <div className="absolute -top-32 right-0 w-[600px] h-[600px] rounded-full bg-[#0055FF]/15 blur-3xl" />

                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
                        {/* Left intro */}
                        <div className="lg:col-span-5">
                            <div className="kicker text-[#00E5FF] mb-6 flex items-center gap-3">
                                <span className="w-8 h-px bg-[#00E5FF]" /> Contact
                            </div>
                            <h1 className="font-display font-black text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                                Let's design <span className="text-gradient-cyan">the wireless layer</span> for your operation.
                            </h1>
                            <p className="mt-8 text-white/70 leading-relaxed max-w-md">
                                Tell us about your industry, your power needs and the use
                                case you'd like to enable. Our engineering team will
                                respond with a scoping conversation.
                            </p>

                            <div className="mt-12 space-y-6">
                                <div className="flex gap-4">
                                    <Mail className="w-5 h-5 text-[#00E5FF] mt-0.5 shrink-0" />
                                    <div>
                                        <div className="kicker text-white/40 mb-1">Email</div>
                                        <a href="mailto:partnerships@elwis.io" className="text-white hover:text-[#00E5FF]">
                                            partnerships@elwis.io
                                        </a>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <MapPin className="w-5 h-5 text-[#00E5FF] mt-0.5 shrink-0" />
                                    <div>
                                        <div className="kicker text-white/40 mb-1">Headquarters</div>
                                        <p className="text-white">
                                            ELWIS Engineering<br />
                                            Future Infrastructure Park<br />
                                            Innovation District
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-12 border-l-2 border-[#00E5FF] pl-5">
                                <p className="text-white/80 italic font-display text-lg">
                                    "Every operation we talk to has the same hidden cost line:
                                    cables, plugs and the time it takes to manage them."
                                </p>
                                <p className="kicker text-white/40 mt-3">— ELWIS Engineering Team</p>
                            </div>
                        </div>

                        {/* Right form */}
                        <div className="lg:col-span-7">
                            <div className="bg-[#0A1220] border border-white/10 p-8 lg:p-12">
                                {submitted ? (
                                    <div data-testid="contact-success" className="py-16 text-center">
                                        <CheckCircle2 className="w-14 h-14 text-[#00E5FF] mx-auto" />
                                        <h2 className="mt-6 font-display font-bold text-3xl">Inquiry received.</h2>
                                        <p className="mt-4 text-white/70 max-w-md mx-auto leading-relaxed">
                                            Thank you. Our engineering team will reach out within
                                            two business days to schedule a scoping conversation.
                                        </p>
                                        <button
                                            type="button"
                                            data-testid="contact-success-reset"
                                            onClick={() => setSubmitted(false)}
                                            className="mt-8 inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 rounded-md text-sm font-semibold hover:border-[#00E5FF] hover:text-[#00E5FF]"
                                        >
                                            Submit another inquiry
                                        </button>
                                    </div>
                                ) : (
                                    <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-6">
                                        <div className="kicker text-[#00E5FF]">Qualified lead form</div>
                                        <h2 className="font-display font-bold text-2xl lg:text-3xl">Tell us about your project</h2>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Field label="Full name" required>
                                                <input
                                                    required
                                                    type="text"
                                                    data-testid="contact-input-name"
                                                    value={form.name}
                                                    onChange={update("name")}
                                                    className={inputCls}
                                                />
                                            </Field>
                                            <Field label="Work email" required>
                                                <input
                                                    required
                                                    type="email"
                                                    data-testid="contact-input-email"
                                                    value={form.email}
                                                    onChange={update("email")}
                                                    className={inputCls}
                                                />
                                            </Field>
                                            <Field label="Company" required>
                                                <input
                                                    required
                                                    type="text"
                                                    data-testid="contact-input-company"
                                                    value={form.company}
                                                    onChange={update("company")}
                                                    className={inputCls}
                                                />
                                            </Field>
                                            <Field label="Country" required>
                                                <input
                                                    required
                                                    type="text"
                                                    data-testid="contact-input-country"
                                                    value={form.country}
                                                    onChange={update("country")}
                                                    className={inputCls}
                                                />
                                            </Field>
                                            <Field label="Industry" required>
                                                <select
                                                    required
                                                    data-testid="contact-input-industry"
                                                    value={form.industry}
                                                    onChange={update("industry")}
                                                    className={inputCls}
                                                >
                                                    {INDUSTRIES.map((i) => (
                                                        <option key={i.slug} value={i.title}>
                                                            {i.title}
                                                        </option>
                                                    ))}
                                                </select>
                                            </Field>
                                            <Field label="Project type" required>
                                                <select
                                                    required
                                                    data-testid="contact-input-project-type"
                                                    value={form.project_type}
                                                    onChange={update("project_type")}
                                                    className={inputCls}
                                                >
                                                    {PROJECT_TYPES.map((p) => (
                                                        <option key={p} value={p}>
                                                            {p}
                                                        </option>
                                                    ))}
                                                </select>
                                            </Field>
                                            <Field label="Required power range" required>
                                                <select
                                                    required
                                                    data-testid="contact-input-power-range"
                                                    value={form.power_range}
                                                    onChange={update("power_range")}
                                                    className={inputCls}
                                                >
                                                    {POWER_RANGES.map((p) => (
                                                        <option key={p} value={p}>
                                                            {p}
                                                        </option>
                                                    ))}
                                                </select>
                                            </Field>
                                            <Field label="Phone (optional)">
                                                <input
                                                    type="tel"
                                                    data-testid="contact-input-phone"
                                                    value={form.phone}
                                                    onChange={update("phone")}
                                                    className={inputCls}
                                                />
                                            </Field>
                                        </div>

                                        <Field label="Describe your use case" required>
                                            <textarea
                                                required
                                                rows={5}
                                                minLength={10}
                                                data-testid="contact-input-use-case"
                                                value={form.use_case}
                                                onChange={update("use_case")}
                                                className={`${inputCls} resize-none`}
                                                placeholder="What assets need to charge? What environment? What's blocking you today?"
                                            />
                                        </Field>

                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            data-testid="contact-submit-button"
                                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#00E5FF] text-[#050B14] px-7 py-4 rounded-md font-semibold hover:bg-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            {submitting ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Sending…
                                                </>
                                            ) : (
                                                <>
                                                    Send Inquiry
                                                    <ArrowRight className="w-4 h-4" />
                                                </>
                                            )}
                                        </button>
                                        <p className="text-xs text-white/40 font-mono uppercase tracking-wider">
                                            By submitting, you agree to be contacted by ELWIS regarding your inquiry.
                                        </p>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

const inputCls =
    "w-full bg-[#050B14] border border-white/15 rounded-md px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] transition-colors";

const Field = ({ label, required, children }) => (
    <label className="block">
        <span className="kicker text-white/50 mb-2 inline-block">
            {label}
            {required && <span className="text-[#00E5FF] ml-1">*</span>}
        </span>
        {children}
    </label>
);

export default Contact;
